import type { ContractOrigination, MyContract } from "../types/myContract";
import type { GeneratedContractDetailJson } from "../types/generatedContract";
import { formatDisplayDate } from "./displayDate";

function readFormString(
  form: Record<string, unknown> | null,
  keys: readonly string[]
): string {
  if (!form) return "";
  for (const k of keys) {
    const v = form[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

function formAnswersAsRecord(
  raw: GeneratedContractDetailJson["form_answers_json"]
): Record<string, unknown> | null {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return null;
}

function mapOrigination(raw: string | null | undefined): ContractOrigination {
  if (raw && raw.toLowerCase() === "external") return "External";
  return "Internal";
}

function formatModifiedBy(
  updatedBy: string | null | undefined,
  createdBy: string,
  // selfId: string | undefined
): { name: string; email: string } {
  const actor = (updatedBy && updatedBy.trim()) || createdBy;
  if (!actor) {
    return { name: "—", email: "" };
  }
  const short =
    actor.length > 12 && /^[0-9a-f-]{36}$/i.test(actor)
      ? `…${actor.slice(-8)}`
      : actor;
  return { name: short, email: "" };
}

/** Prefer API-resolved user profile; fall back to UUID-based placeholder. */
function modifiedByFromApiFields(
  row: GeneratedContractDetailJson,
): { name: string; email: string } {
  const hasUpdated = !!(row.updated_by && row.updated_by.trim());
  const nameRaw = hasUpdated
    ? (row.updated_by_name ?? "").trim() ||
      (row.updated_by_email ?? "").trim()
    : (row.created_by_name ?? "").trim() ||
      (row.created_by_email ?? "").trim();
  const emailRaw = hasUpdated
    ? (row.updated_by_email ?? "").trim()
    : (row.created_by_email ?? "").trim();

  if (nameRaw || emailRaw) {
    const displayName = nameRaw || emailRaw;
    const displayEmail =
      emailRaw && emailRaw !== displayName ? emailRaw : "";
    return { name: displayName, email: displayEmail };
  }

  return formatModifiedBy(row.updated_by, row.created_by);
}

function formatModifiedDate(iso: string | undefined): string {
  if (!iso) return "—";
  return formatDisplayDate(iso);
}

export function mapGeneratedContractToMyContract(
  row: GeneratedContractDetailJson
): MyContract {
  const form = formAnswersAsRecord(row.form_answers_json);

  const contractName = readFormString(form, ["contractName", "contract_name"]);
  const display =
    (row.display_name && row.display_name.trim()) ||
    contractName ||
    `Generated contract`;

  const hasDocExtension = /\.[a-z0-9]{2,8}$/i.test(display);
  const documentName = hasDocExtension ? display : `${display}.docx`;

  const fromFormType = readFormString(form, ["contractType", "contract_type"]);
  const contractType =
    (row.contract_type && row.contract_type.trim()) || fromFormType || "—";
  const fromFormBu = readFormString(form, ["businessUnit", "business_unit"]);
  const businessUnit =
    (row.stored_file?.business_unit && row.stored_file.business_unit.trim()) ||
    fromFormBu ||
    "—";

  const { name: modifiedBy, email: modifiedByEmail } = modifiedByFromApiFields(
    row
  );

  // const wordDesktop =
  //   typeof row.word_desktop_open_url === "string" && row.word_desktop_open_url.trim()
  //     ? row.word_desktop_open_url.trim()
  //     : null;
  // const fileDl =
  //   typeof row.file_download_url === "string" && row.file_download_url.trim()
  //     ? row.file_download_url.trim()
  //     : null;

  return {
    id: row.id,
    documentName,
    contractType,
    contractOrigination: mapOrigination(row.contract_origination),
    businessUnit,
    status: (row.status ?? "").trim().replace(/_/g, " ").toUpperCase(),
    modifiedBy,
    modifiedByEmail,
    modifiedDate: formatModifiedDate(row.updated_at),
    // wordDesktopOpenUrl: wordDesktop,
    // fileDownloadUrl: fileDl
  };
}
